package songwritersnotebook.app.viewmodels

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import songwritersnotebook.app.data.RoadmapItem
import songwritersnotebook.app.data.RoadmapComment
import songwritersnotebook.app.data.RoadmapItemType
import songwritersnotebook.app.data.RoadmapStatus
import songwritersnotebook.app.database.RoadmapRepository
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.auth.auth
import kotlinx.coroutines.flow.*
import kotlinx.coroutines.launch

enum class RoadmapSortBy { NEWEST, TOP_VOTED }

data class RoadmapUiState(
    val items: List<RoadmapItem> = emptyList(),
    val isLoading: Boolean = false,
    val error: String? = null,
    val userVotes: Map<String, Int> = emptyMap(), // ItemId -> VoteType (1, -1)
    val isAdmin: Boolean = false,
    val currentUserId: String? = null,
    val showAddDialog: Boolean = false,
    val searchQuery: String = "",
    val sortBy: RoadmapSortBy = RoadmapSortBy.TOP_VOTED,
    val comments: Map<String, List<RoadmapComment>> = emptyMap()
)

class RoadmapViewModel : ViewModel() {
    private val repository = RoadmapRepository()
    private val auth = Firebase.auth
    
    private val _uiState = MutableStateFlow(RoadmapUiState())
    val uiState: StateFlow<RoadmapUiState> = _uiState.asStateFlow()

    init {
        val user = auth.currentUser
        _uiState.update { it.copy(currentUserId = user?.uid, isLoading = true) }
        
        viewModelScope.launch {
            repository.getRoadmapItems()
                .catch { e -> _uiState.update { it.copy(error = e.message, isLoading = false) } }
                .collect { items ->
                    _uiState.update { it.copy(items = items, isLoading = false) }
                    // Monitor votes for each item if logged in
                    user?.let { u ->
                        items.forEach { item ->
                            observeVote(item.id, u.uid)
                        }
                    }
                }
        }
    }

    private fun observeVote(itemId: String, userId: String) {
        viewModelScope.launch {
            repository.getUserVote(itemId, userId).collect { voteType ->
                _uiState.update { state ->
                    val newVotes = state.userVotes.toMutableMap()
                    if (voteType != null) newVotes[itemId] = voteType else newVotes.remove(itemId)
                    state.copy(userVotes = newVotes)
                }
            }
        }
    }

    fun setAdminStatus(isAdmin: Boolean) {
        _uiState.update { it.copy(isAdmin = isAdmin) }
    }

    fun toggleAddDialog(show: Boolean) {
        _uiState.update { it.copy(showAddDialog = show) }
    }

    fun addItem(title: String, description: String, type: RoadmapItemType) {
        val currentUserId = _uiState.value.currentUserId ?: return
        viewModelScope.launch {
            try {
                repository.addRoadmapItem(title, description, type, currentUserId)
                toggleAddDialog(false)
            } catch (e: Exception) {
                _uiState.update { it.copy(error = "Failed to add item: ${e.message}") }
            }
        }
    }

    fun mergeItem(sourceItem: RoadmapItem, targetItemId: String) {
        if (!_uiState.value.isAdmin) return
        viewModelScope.launch {
            try {
                repository.mergeItems(sourceItem.id, targetItemId)
            } catch (e: Exception) {
                _uiState.update { it.copy(error = "Failed to merge item: ${e.message}") }
            }
        }
    }

    fun updateStatus(item: RoadmapItem, newStatus: RoadmapStatus) {
        if (!_uiState.value.isAdmin) return
        viewModelScope.launch {
            repository.updateRoadmapItemStatus(item.id, newStatus)
        }
    }

    fun updateItemDetails(item: RoadmapItem, title: String, description: String, type: RoadmapItemType) {
        if (!_uiState.value.isAdmin) return
        viewModelScope.launch {
            try {
                repository.updateRoadmapItemDetails(item.id, title, description, type)
            } catch (e: Exception) {
                _uiState.update { it.copy(error = "Failed to update item: ${e.message}") }
            }
        }
    }

    fun deleteItem(item: RoadmapItem) {
        if (!_uiState.value.isAdmin) return
        viewModelScope.launch {
            repository.deleteRoadmapItem(item.id)
        }
    }

    fun vote(item: RoadmapItem, voteType: Int) {
        val userId = _uiState.value.currentUserId ?: return
        val currentVote = _uiState.value.userVotes[item.id] ?: 0
        
        // If clicking same vote type, remove it (0)
        val finalVoteType = if (currentVote == voteType) 0 else voteType
        
        viewModelScope.launch {
            try {
                repository.vote(item.id, userId, finalVoteType)
            } catch (e: Exception) {
                _uiState.update { it.copy(error = "Voting failed: ${e.message}") }
            }
        }
    }

    fun setSearchQuery(query: String) {
        _uiState.update { it.copy(searchQuery = query) }
    }

    fun setSortBy(sortBy: RoadmapSortBy) {
        _uiState.update { it.copy(sortBy = sortBy) }
    }

    fun loadComments(itemId: String) {
        viewModelScope.launch {
            repository.getCommentsFlow(itemId).collect { commentList ->
                _uiState.update { state ->
                    val newCommentsMap = state.comments.toMutableMap()
                    newCommentsMap[itemId] = commentList
                    state.copy(comments = newCommentsMap)
                }
            }
        }
    }

    fun addComment(itemId: String, text: String) {
        val uid = _uiState.value.currentUserId ?: return
        viewModelScope.launch {
            try {
                repository.addComment(itemId, uid, text)
            } catch (e: Exception) {
                _uiState.update { it.copy(error = "Failed to add comment: ${e.message}") }
            }
        }
    }

    fun deleteComment(itemId: String, commentId: String) {
        viewModelScope.launch {
            try {
                repository.deleteComment(itemId, commentId)
            } catch (e: Exception) {
                _uiState.update { it.copy(error = "Failed to delete comment: ${e.message}") }
            }
        }
    }
}
